import type { ApiAuthMode, ApiRequestOptions, IApiService } from './types';
import type { IAuthSessionStore, ITokenRefreshService, StoredSessionData } from '../auth/types';
import type { ILogger } from '../log/types';

export interface ApiServiceConfig {
  sessionStore: IAuthSessionStore;
  tokenService: ITokenRefreshService;
  logger: ILogger;
}

export class ApiService implements IApiService {
  private readonly config: ApiServiceConfig;
  private readonly logger: ILogger;

  constructor(config: ApiServiceConfig) {
    this.config = config;
    this.logger = config.logger;
  }

  // Public HTTP methods
  async get<T>(url: string, init?: ApiRequestOptions): Promise<T> {
    return this.request<T>('GET', url, undefined, init);
  }

  async post<T, B = unknown>(url: string, body?: B, init?: ApiRequestOptions): Promise<T> {
    return this.request<T>('POST', url, body, init);
  }

  async put<T, B = unknown>(url: string, body?: B, init?: ApiRequestOptions): Promise<T> {
    return this.request<T>('PUT', url, body, init);
  }

  async patch<T, B = unknown>(url: string, body?: B, init?: ApiRequestOptions): Promise<T> {
    return this.request<T>('PATCH', url, body, init);
  }

  async delete<T>(url: string, init?: ApiRequestOptions): Promise<T> {
    return this.request<T>('DELETE', url, undefined, init);
  }

  //
  // Core request pipeline ("interceptor"-style)
  //

  private async request<T>(method: string, url: string, body?: unknown, init?: ApiRequestOptions): Promise<T> {
    const authMode: ApiAuthMode = init?.authMode ?? 'required';
    const suppressLogging = init?.suppressLogging ?? false;
    const requestOptions: RequestInit = { ...(init ?? {}) };
    delete (requestOptions as RequestInit & { authMode?: ApiAuthMode }).authMode;
    delete (requestOptions as RequestInit & { suppressLogging?: boolean }).suppressLogging;
    let requestInit: RequestInit = {
      ...requestOptions,
      method,
      headers: this.mergeHeaders(requestOptions.headers, {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };

    if (!suppressLogging) {
      this.logger.debug(`[ApiService] ${method} ${url}`, { requestInit });
    }

    // 1) Ensure valid token & attach Authorization header when required
    if (authMode === 'required') {
      requestInit = await this.withAuthHeader(requestInit);
    }

    // 2) Fire request
    const response = await fetch(url, requestInit);

    if (!suppressLogging) {
      this.logger.debug(`[ApiService] Response from ${method} ${url}`, { status: response.status });
    }

    // 4) Handle success/error
    if (!response.ok) {
      throw await this.buildError(response);
    }

    const responseData: T = await this.parseJson<T>(response);

    if (!suppressLogging) {
      this.logger.debug(`[ApiService] Parsed response from ${method} ${url}`, { responseData });
    }
    return responseData;
  }

  //
  // Auth & token handling
  //

  private async withAuthHeader(init: RequestInit): Promise<RequestInit> {
    const tokens = await this.ensureValidTokens(); // interceptor: refresh if expired
    if (!tokens?.accessToken) {
      throw new Error('[ApiService] No valid access token available');
    }

    return {
      ...init,
      headers: this.mergeHeaders(init.headers, {
        Authorization: `Bearer ${tokens.accessToken}`,
      }),
    };
  }

  // Check expiration; if expired, refresh
  private async ensureValidTokens(): Promise<StoredSessionData | null> {
    return await this.config.tokenService.ensureValidToken();
  }

  //
  // Helpers
  //

  private async parseJson<T>(response: Response): Promise<T> {
    const text = await response.text();
    if (!text) {
      // @ts-expect-error allow void
      return undefined;
    }
    return JSON.parse(text) as T;
  }

  private async buildError(response: Response): Promise<Error> {
    const text = await response.text().catch(() => '');
    let message = text || `HTTP ${response.status} ${response.statusText || ''}`.trim();

    if (text) {
      try {
        const parsed = JSON.parse(text) as { message?: string };
        if (typeof parsed.message === 'string' && parsed.message.trim()) {
          message = parsed.message.trim();
        }
      } catch {
        // Keep raw response text when the body is not JSON.
      }
    }

    const error = new Error(message);
    // Attach details for debugging using type assertion
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error as any).status = response.status;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error as any).body = text;
    return error;
  }

  private mergeHeaders(base?: HeadersInit, extra?: HeadersInit): HeadersInit | undefined {
    if (!base && !extra) return undefined;

    const result = new Headers(base || {});
    if (extra) {
      const extraHeaders = new Headers(extra);
      extraHeaders.forEach((value, key) => {
        result.set(key, value);
      });
    }
    return result;
  }
}
